<template>
  <div class="enlaces-page">
    <PageHeader title="Enlaces de estudios" subtitle="Comparte una selección de estudios públicamente con un enlace">
      <template #actions>
        <Button icon="pi pi-plus" label="Nuevo enlace" @click="showCreate = true" />
      </template>
    </PageHeader>

    <div class="enlaces-content">
      <div v-if="loading" class="text-center py-5 text-500">Cargando…</div>

      <EmptyState
        v-else-if="!enrichedTokens.length"
        icon="pi-link"
        title="Sin enlaces"
        description="Crea uno para compartir estudios públicamente."
      >
        <template #action>
          <Button icon="pi pi-plus" label="Nuevo enlace" @click="showCreate = true" />
        </template>
      </EmptyState>

      <div v-else class="flex flex-column gap-3">
        <div v-for="t in enrichedTokens" :key="t.token" class="enlace-card">
          <div class="flex align-items-start gap-3">
            <!-- Info -->
            <div class="flex-1 min-w-0">
              <div class="text-lg font-semibold mb-2">{{ t.name }}</div>

              <!-- Fechas -->
              <div class="flex align-items-center gap-2 text-sm mb-2">
                <i class="pi pi-calendar text-500" />
                <span v-if="t.dateRange" class="text-700">{{ t.dateRange }}</span>
                <span v-else class="text-400">Sin estudios asociados</span>
              </div>

              <!-- Grupos -->
              <div v-if="t.gruposNames.length" class="flex flex-wrap gap-1 mb-2">
                <Tag v-for="g in t.gruposNames" :key="g" :value="g" severity="secondary" />
              </div>

              <!-- Estudios count -->
              <div class="text-xs text-500 mb-2">
                {{ t.estudioIds.length }} {{ t.estudioIds.length === 1 ? 'estudio' : 'estudios' }} incluidos
              </div>

              <!-- Link -->
              <div class="flex align-items-center gap-1 mt-2">
                <span
                  class="text-xs text-400 font-mono overflow-hidden text-overflow-ellipsis white-space-nowrap flex-1"
                  style="max-width:340px"
                >
                  {{ publicUrl(t.token) }}
                </span>
                <Button
                  :icon="copied === t.token ? 'pi pi-check' : 'pi pi-copy'"
                  size="small"
                  text
                  :severity="copied === t.token ? 'success' : 'secondary'"
                  v-tooltip="'Copiar enlace'"
                  @click="copyLink(t.token)"
                />
              </div>
            </div>

            <!-- Acciones -->
            <div class="flex flex-column gap-2 flex-shrink-0">
              <Button
                icon="pi pi-list-check"
                label="Gestionar estudios"
                size="small"
                severity="success"
                outlined
                @click="openManage(t)"
              />
              <Button
                icon="pi pi-share-alt"
                label="Metadata"
                size="small"
                severity="secondary"
                outlined
                v-tooltip="'Cómo se ve al compartirlo'"
                @click="openMetadata(t)"
              />
              <Button
                icon="pi pi-trash"
                size="small"
                severity="danger"
                text
                v-tooltip="'Eliminar enlace'"
                @click="confirmDelete(t)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog: crear enlace -->
    <Dialog v-model:visible="showCreate" header="Nuevo enlace" modal style="width:380px">
      <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
          <label class="text-sm">Nombre del enlace</label>
          <InputText
            v-model="newName"
            placeholder="Ej: Estudios del grupo Norte..."
            autofocus
            @keyup.enter="doCreate"
          />
        </div>
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showCreate = false" />
          <Button label="Crear" :disabled="!newName.trim()" :loading="creating" @click="doCreate" />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: gestionar estudios incluidos en el enlace -->
    <Dialog
      v-model:visible="showManage"
      :header="`Gestionar estudios — ${manageToken?.name ?? ''}`"
      modal
      style="width:520px;max-width:96vw"
    >
      <div class="flex flex-column gap-3">
        <div class="flex gap-2 flex-wrap">
          <InputText v-model="manageSearch" placeholder="Buscar por publicador, estudiante o dirección…" class="flex-1" style="min-width:200px" />
          <Dropdown
            v-model="manageZonaId"
            :options="zonaOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Todas las congregaciones"
            filter
            showClear
            class="w-15rem"
          />
        </div>

        <div class="flex align-items-center justify-content-between">
          <span class="text-xs text-500">
            {{ estudiosFiltrados.length }} {{ estudiosFiltrados.length === 1 ? 'resultado' : 'resultados' }}
          </span>
          <Button
            :label="todosFiltradosSeleccionados ? 'Quitar selección de filtrados' : 'Seleccionar todos los filtrados'"
            :icon="todosFiltradosSeleccionados ? 'pi pi-times' : 'pi pi-check-square'"
            size="small"
            text
            :disabled="!estudiosFiltrados.length"
            :loading="bulkWorking"
            @click="toggleSeleccionarTodosFiltrados"
          />
        </div>

        <div v-if="!estudiosFiltrados.length" class="text-center text-500 text-sm py-3">
          No hay estudios que coincidan con la búsqueda.
        </div>

        <div v-else class="manage-list">
          <div v-for="e in estudiosFiltrados" :key="e.id" class="manage-item">
            <Checkbox
              :modelValue="manageSelected.has(e.id)"
              binary
              :disabled="togglingId === e.id || bulkWorking"
              @update:modelValue="toggleEstudio(e)"
            />
            <div class="flex-1 min-w-0" @click="toggleEstudio(e)">
              <div class="font-medium text-sm">{{ e.estudiante || e.publicador || 'Sin nombre' }}</div>
              <div class="text-xs text-500 overflow-hidden text-overflow-ellipsis white-space-nowrap">
                {{ e.direccion || 'Sin dirección' }}
              </div>
            </div>
            <Tag :value="tipoInfo(e.tipo).label" :severity="tipoInfo(e.tipo).severity" />
          </div>
        </div>
      </div>
      <template #footer>
        <Button label="Listo" @click="showManage = false" />
      </template>
    </Dialog>

    <ConfirmDialog />
  </div><!-- /enlaces-page -->
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouterStore } from '@/store/router'
import { useEstudios } from '@/composables/useEstudios'
import { useGruposInvitados } from '@/composables/useGruposInvitados'
import { useCatalogos } from '@/composables/useCatalogos'
import { useEstudioDashboardTokens } from '@/composables/useEstudioDashboardTokens'
import { useConfirm } from 'primevue/useconfirm'
import Button from 'primevue/button'
import PageHeader from '@/components/ui/PageHeader.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import Checkbox from 'primevue/checkbox'
import Tag from 'primevue/tag'
import ConfirmDialog from 'primevue/confirmdialog'

const route = useRouterStore()
const confirm = useConfirm()
const {
  getPublicUrl,
  getTokens,
  createToken,
  revokeToken,
  addEstudioToToken,
  removeEstudioFromToken,
} = useEstudioDashboardTokens()
const { getEstudios } = useEstudios()
const { getGruposInvitados } = useGruposInvitados()
const { getZonas } = useCatalogos()

const TIPO_OPCIONES = [
  { value: 'visita_inicial', label: 'Visita inicial', severity: 'secondary' },
  { value: 'revisita', label: 'Revisita', severity: 'warn' },
  { value: 'estudio_iniciado', label: 'Estudio iniciado', severity: 'info' },
  { value: 'estudio_establecido', label: 'Estudio establecido', severity: 'success' },
]
const tipoInfo = (tipo) => TIPO_OPCIONES.find((t) => t.value === tipo) || TIPO_OPCIONES[2]

// ── Estado principal ─────────────────────────────────────────────────────────
const loading = ref(true)
const tokens = ref([])
const estudios = ref([])
const guestGroups = ref([])
const zonas = ref([])

const publicUrl = (token) => getPublicUrl(token)

const guestGroupLabel = (guestGroupId) =>
  guestGroups.value.find((g) => g.id === guestGroupId)?.codigo || ''

// Una "zona" es, en este modelo, el catálogo de "otras congregaciones" que traen
// grupos de invitados (mismo concepto que el filtro "por congregación" de EstudiosTerritorio.vue).
const zonaIdDeGrupo = (guestGroupId) => guestGroups.value.find((g) => g.id === guestGroupId)?.zonaId || null
const zonaOptions = computed(() => zonas.value.map((z) => ({ label: z.name, value: z.id })))

const formatFecha = (f) => {
  if (!f) return null
  const [y, m, d] = f.split('-')
  return y && m && d ? new Date(Number(y), Number(m) - 1, Number(d)) : null
}

const enrichedTokens = computed(() => {
  return tokens.value.map((t) => {
    const linkedEstudios = estudios.value.filter((e) => t.estudioIds.includes(e.id))

    let dateRange = null
    const dates = linkedEstudios.map((e) => formatFecha(e.fechaInicio)).filter(Boolean).sort((a, b) => a - b)
    if (dates.length) {
      const fmt = (d) => d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      dateRange = dates.length === 1 ? fmt(dates[0]) : `${fmt(dates[0])} → ${fmt(dates[dates.length - 1])}`
    }

    const gruposNames = [...new Set(linkedEstudios.map((e) => guestGroupLabel(e.guestGroupId)).filter(Boolean))]

    return { ...t, dateRange, gruposNames }
  })
})

// ── Carga inicial ─────────────────────────────────────────────────────────────
const loadAll = async () => {
  loading.value = true
  const [tokensRes, estudiosRes, gruposRes, zonasRes] = await Promise.all([
    getTokens(),
    getEstudios(),
    getGruposInvitados(),
    getZonas(),
  ])
  tokens.value = tokensRes.tokens || []
  estudios.value = estudiosRes.estudios || []
  guestGroups.value = gruposRes.guestGroups || []
  zonas.value = zonasRes.zonas || []
  loading.value = false
}

onMounted(loadAll)

// ── Crear enlace ──────────────────────────────────────────────────────────────
const showCreate = ref(false)
const newName = ref('')
const creating = ref(false)

const doCreate = async () => {
  if (!newName.value.trim()) return
  creating.value = true
  const res = await createToken(newName.value.trim())
  if (res.token) {
    tokens.value.unshift(res)
    newName.value = ''
    showCreate.value = false
    openManage(res)
  }
  creating.value = false
}

// ── Eliminar enlace ───────────────────────────────────────────────────────────
const confirmDelete = (t) => {
  confirm.require({
    message: `¿Eliminar el enlace "${t.name}"? Los usuarios que tengan el link ya no podrán acceder.`,
    header: 'Confirmar eliminación',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Eliminar',
    rejectLabel: 'Cancelar',
    accept: async () => {
      await revokeToken(t.token)
      tokens.value = tokens.value.filter((x) => x.token !== t.token)
    },
  })
}

// ── Copiar link ───────────────────────────────────────────────────────────────
const copied = ref(null)

const copyLink = (token) => {
  navigator.clipboard.writeText(getPublicUrl(token))
  copied.value = token
  setTimeout(() => { if (copied.value === token) copied.value = null }, 2000)
}

// ── Editar la metadata de este enlace ─────────────────────────────────────────
const openMetadata = (t) => {
  route.page = 'metadataEnlaces'
  route.params = { type: 'estudiosDashboard', token: t.token }
}

// ── Gestionar estudios incluidos en el enlace ─────────────────────────────────
const showManage = ref(false)
const manageToken = ref(null)
const manageSearch = ref('')
const manageZonaId = ref(null)
const togglingId = ref(null)
const bulkWorking = ref(false)

const manageSelected = computed(() => new Set(manageToken.value?.estudioIds || []))

// Sin filtros aplicados (búsqueda vacía y "Todas las congregaciones") se listan
// todos los estudios de la congregación, no sólo los que ya tienen zona/grupo asignado.
const estudiosFiltrados = computed(() => {
  const q = manageSearch.value.trim().toLowerCase()
  return estudios.value
    .filter((e) => !manageZonaId.value || zonaIdDeGrupo(e.guestGroupId) === manageZonaId.value)
    .filter((e) => !q || [e.publicador, e.estudiante, e.direccion].some((v) => (v || '').toLowerCase().includes(q)))
})

const todosFiltradosSeleccionados = computed(() =>
  estudiosFiltrados.value.length > 0 && estudiosFiltrados.value.every((e) => manageSelected.value.has(e.id))
)

const openManage = (t) => {
  manageToken.value = t
  manageSearch.value = ''
  manageZonaId.value = null
  showManage.value = true
}

const toggleEstudio = async (e) => {
  if (!manageToken.value || togglingId.value || bulkWorking.value) return
  const alreadySelected = manageSelected.value.has(e.id)
  togglingId.value = e.id
  try {
    const res = alreadySelected
      ? await removeEstudioFromToken(manageToken.value.token, e.id)
      : await addEstudioToToken(manageToken.value.token, e.id)
    if (res.estudioIds) {
      manageToken.value.estudioIds = res.estudioIds
      const t = tokens.value.find((x) => x.token === manageToken.value.token)
      if (t) t.estudioIds = res.estudioIds
    }
  } finally {
    togglingId.value = null
  }
}

// Selecciona (o quita) de una sola vez todos los estudios que coinciden con la
// búsqueda + filtro por congregación actuales, sin tener que tildar uno por uno.
const toggleSeleccionarTodosFiltrados = async () => {
  if (!manageToken.value || bulkWorking.value || !estudiosFiltrados.value.length) return
  const seleccionar = !todosFiltradosSeleccionados.value
  const pendientes = estudiosFiltrados.value.filter((e) =>
    seleccionar ? !manageSelected.value.has(e.id) : manageSelected.value.has(e.id)
  )
  if (!pendientes.length) return

  bulkWorking.value = true
  try {
    await Promise.all(
      pendientes.map((e) =>
        seleccionar
          ? addEstudioToToken(manageToken.value.token, e.id)
          : removeEstudioFromToken(manageToken.value.token, e.id)
      )
    )
    const ids = new Set(manageToken.value.estudioIds)
    for (const e of pendientes) {
      if (seleccionar) ids.add(e.id)
      else ids.delete(e.id)
    }
    const nuevos = [...ids]
    manageToken.value.estudioIds = nuevos
    const t = tokens.value.find((x) => x.token === manageToken.value.token)
    if (t) t.estudioIds = nuevos
  } finally {
    bulkWorking.value = false
  }
}
</script>

<style scoped>
.enlaces-page {
  width: 100%;
  text-align: left;
}
.enlaces-content {
  padding: 0 var(--space-5) var(--space-5);
}
.enlace-card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--space-4) var(--space-5);
  box-shadow: var(--shadow-1);
}
.manage-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 50vh;
  overflow-y: auto;
}
.manage-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 6px;
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
}
.manage-item:hover {
  background: var(--bg-hover, rgba(0, 0, 0, 0.03));
}
</style>
